<script setup lang="ts">
// No default `label`: component-lib has no i18n of its own, so a hardcoded
// string here would announce one language to every locale (issue 195, DR-19a).
// A caller that needs the spinner announced supplies a translated `label`; a
// caller whose spinner sits beside text that already says what's happening
// passes nothing, and `role="status"` renders with no `aria-label` at all.
//
// Three dots pulsing in sequence (#447), in place of the spinning ring. This is the
// IN-PLACE wait (inside a button, a row, a line of text, a field); a wait that owns a
// whole region — a page, a panel, a modal body, a first load — is `LumiLoader`. The dots
// are decorative — the `role="status"` on the wrapper (plus the caller's `label`)
// is what carries the meaning, so they are hidden from assistive tech rather than
// read out three times.
//
// The template is deliberately SINGLE-ROOTED with no leading comment: a comment
// node before the root makes the component a fragment, and `wrapper.attributes()`
// (and every host that binds a class or an attr onto it) then finds nothing.
withDefaults(defineProps<{ size?: 'sm' | 'md'; label?: string }>(), {
  size: 'md',
});
</script>

<template>
  <span class="lumi-spinner" :class="`lumi-spinner--${size}`" role="status" :aria-label="label">
    <span class="lumi-spinner__dot" aria-hidden="true" />
    <span class="lumi-spinner__dot" aria-hidden="true" />
    <span class="lumi-spinner__dot" aria-hidden="true" />
  </span>
</template>

<style>
/* Durations and easings come from tokens/motion.css (`check-ds-tokens` refuses an
   undeclared var); the colour is `--accent`, which in the Sand & Espresso palette
   resolves to `--btn-primary-bg` — the same ink the ring this replaces used for
   its moving arc. The dip is opacity rather than a second token, so the dots read
   as one thing pulsing rather than three things in two colours.
   `sm` is the BUTTON variant — it sits inside a pressed control, so it stays
   compact and calm; `md` is the in-place wait beside or inside something larger than a button. */
.lumi-spinner{display:inline-flex;align-items:center;gap:0.28em;flex-shrink:0;line-height:0;}
.lumi-spinner__dot{display:block;border-radius:50%;background:var(--accent);animation:lumi-spinner-pulse var(--dur-loading) var(--ease-standard) infinite;}
.lumi-spinner__dot:nth-child(1){animation-delay:0ms;}
.lumi-spinner__dot:nth-child(2){animation-delay:150ms;}
.lumi-spinner__dot:nth-child(3){animation-delay:300ms;}
.lumi-spinner--sm{height:16px;}
.lumi-spinner--sm .lumi-spinner__dot{width:4px;height:4px;}
.lumi-spinner--md{height:32px;}
.lumi-spinner--md .lumi-spinner__dot{width:7px;height:7px;}

/* THE FROZEN FIRST FRAME MUST STILL READ AS "LOADING". The global
   prefers-reduced-motion block clamps every animation to 0.01ms, which paints
   whatever the keyframes say at 0% — so no dot may start at opacity 0, or a
   reduced-motion reader sees a blank space where the spinner is. All three start
   visible; the animation dips them, it does not build them up. */
@keyframes lumi-spinner-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.72); }
}
</style>
